import { z } from "zod/v4"
import type { DocumentSnapshotLike } from "../firestore"
import { SystemNotificationId } from "../id"
import { Period, PeriodRequest, PeriodState } from "./common"
import { QuillDeltaServerSchema } from "./quill-delta"
import { CreatedAt, UpdatedAt } from "./system"

export enum SystemNotificationLaunchPopup {
  Highest = "highest",
  Lowest = "lowest",
}

export const SystemNotificationLaunchPopupSchema = z.enum(SystemNotificationLaunchPopup).nullable()


export const SystemNotificationDocumentMeta = z.object({
  title: z.string().describe("通知のタイトル"),
  content: z.any().describe("通知の内容（QuillのDelta形式）"),
  status: z.union([z.literal("public"), z.literal("hidden")]).describe("公開状態"),
  launch_popup: SystemNotificationLaunchPopupSchema.describe("起動時ポップアップの表示設定"),
  launch_popup_period: Period.nullable().describe("起動時お知らせを表示する期間（nullの場合は起動時お知らせなし）"),
  display_period: Period.describe("表示期間"),
  top_image_id: z.string().nullable().describe("TOP画像のID（ManagedImageId）"),
})

export type SystemNotificationDocumentMeta = z.infer<typeof SystemNotificationDocumentMeta>

export const SystemNotificationDocumentBody = z.intersection(
  SystemNotificationDocumentMeta,
  z.object({
    top_image_url: z.string().nullable().describe("TOP画像のURL（top_image_idから自動計算）"),
    reference_managed_image_ids: z.array(z.string()).describe("使用している画像のIDリスト（自動計算）"),
    display_period_state: PeriodState.describe("表示期間の状態（システム自動計算）"),
    launch_popup_period_state: PeriodState.nullable().describe("起動時お知らせ表示期間の状態（システム自動計算、nullの場合は起動時お知らせなし）"),
    created_at: CreatedAt,
    updated_at: UpdatedAt,
  })
)

export type SystemNotificationDocumentBody = z.infer<typeof SystemNotificationDocumentBody>

export type SystemNotificationDocument = SystemNotificationDocumentBody & {
  id: SystemNotificationId,
}

export class SystemNotificationDocuments {

  /** お知らせ内画像の CDN 変換パラメータ: 1080px, q-15, f-webp */
  static readonly IMAGE_TRANSFORMATION = "w-1080,q-15,f-webp"

  static toDocumentData(notification: SystemNotificationDocument): object {
    return {
      ...notification,
      // id は document path に入るので入れない
      id: undefined,
    }
  }

  static fromDocumentSnapshot(doc: DocumentSnapshotLike | null): SystemNotificationDocument | null {
    if (!doc) {
      return null
    }
    const data = doc.data()
    if (data === undefined) {
      return null
    }

    return {
      id: SystemNotificationId(doc.id),
      title: data["title"],
      content: data["content"],
      status: data["status"],
      launch_popup: data["launch_popup"],
      display_period: data["display_period"],
      display_period_state: data["display_period_state"],
      launch_popup_period: data["launch_popup_period"],
      launch_popup_period_state: data["launch_popup_period_state"],
      top_image_id: data["top_image_id"],
      top_image_url: data["top_image_url"],
      reference_managed_image_ids: data["reference_managed_image_ids"] || [],
      created_at: data["created_at"],
      updated_at: data["updated_at"],
    }
  }
}

export const CreateSystemNotificationRequest = z.object({
  title: z.string(),
  content: QuillDeltaServerSchema,
  status: z.union([z.literal("public"), z.literal("hidden")]),
  launchPopup: SystemNotificationLaunchPopupSchema,
  displayPeriod: PeriodRequest.describe("表示期間"),
  launchPopupPeriod: PeriodRequest.optional().describe("起動時お知らせを表示する期間（省略時はdisplayPeriodと同一）"),
  topImageId: z.string().optional().describe("TOP画像のID（ManagedImageId）"),
})

export type CreateSystemNotificationRequest = z.infer<typeof CreateSystemNotificationRequest>

export const UpdateSystemNotificationRequest = z.object({
  title: z.string(),
  content: QuillDeltaServerSchema,
  status: z.union([z.literal("public"), z.literal("hidden")]),
  launchPopup: SystemNotificationLaunchPopupSchema,
  displayPeriod: PeriodRequest.describe("表示期間"),
  launchPopupPeriod: PeriodRequest.optional().describe("起動時お知らせを表示する期間（省略時はdisplayPeriodと同一）"),
  topImageId: z.string().optional().describe("TOP画像のID（ManagedImageId）"),
})

export type UpdateSystemNotificationRequest = z.infer<typeof UpdateSystemNotificationRequest>
